import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { map, take, catchError, delay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { LoggingMessageService } from 'src/app/core/services/logging-message.service';
import { environment } from 'src/environments/environment';
import { DataResult } from '../models/data-result.model';
import { UserDetails } from '../site-management/site-coordinator/models/user-details.model';
import { UserDetailResult } from '../site-management/site-coordinator/models/user-result.model';
import { GridDetailResult } from '../site-management/site-coordinator/models/grid-result.model';
import { StorageService } from 'src/app/core/services/storage.service';
import { AuthService } from './auth.service';


@Injectable()
export class SiteManagementService {
  private _BASE_URL = environment.BusinessApi;
  private _LOCAL_API = environment.LocalJsonApi;
  private _LOCAL_USER_LIST_FILENAME = 'site-management-userlist.json';
  private _LOCAL_USER_ROLE_LIST_FILENAME = 'site-management-userrolelist.json';
  private _LOCAL_MAIN_MENU_ACCESS_FILENAME = 'site-management-mainmenuaccess.json';
  private _LOCAL_DATA_ENTRY_ACCESS_FILENAME = 'site-management-dataentryaccess.json';
  private _LOCAL_SAVE_USER_FILENAME = 'site-management-saveuser.json';
  private _LOCAL_DELETE_USER_FILENAME = 'site-management-deleteuser.json';
  public _NO_DATA = { data: [] };
  public message: {
    text: string;
    class: string;
  } = {
    text: '',
    class: ''
  };


  constructor(private http: HttpClient, private logger: LoggingMessageService, private authService: AuthService, private sessionService: StorageService) { }

  
  showAPIMessage(message: string, messageClass: string) {
    this.message = {
      text: message,
      class: messageClass
    };
  }

  getAPIMessage(message: string, messageClass: string): any {
    this.message = {
      text: message,
      class: messageClass
    };
    return this.message;
  }

  
  // clearAPIMessage() {
  //   this.message = null;
  // }

  // GET User List
  public getUserList(hospid: number): Observable<DataResult> {
    if (environment.UseBusinessServerForSiteManagement) {
      return this.getBusinessUserList(hospid);
    } else {
      return this.getLocalUserList(hospid);
    }
  }

  private getLocalUserList(hospid: number): Observable<DataResult> {
    const uri = `${this._LOCAL_API}${this._LOCAL_USER_LIST_FILENAME}`;

    return this.http.get<DataResult>(
      uri, { observe: 'response' }
    )
      .pipe(
        map(response => {

          delay(5000);

          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }

          return result;
        }),
        catchError(error => {
          return this.handleError(`getLocalUserList failed`, error, hospid);
        }),
        take(1)
      );
  }

  private getBusinessUserList(hospid: number): Observable<DataResult> {
    const uri = `${this._BASE_URL}SiteCoordinator/GetUsers?facID=${hospid}`;
    const media = environment.GetUsersMedia;
    // const media = 'application/json';
    return this.http
      .get<DataResult>(uri,
        {
          headers: { Accept: media },
          observe: 'response'
        }
      )
      .pipe(
        map(response => {
          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }

          return result;
        }),
        catchError(error => {
          return this.handleError(`getBusinessUserList failed`, error, hospid);
        }),
        take(1)
      );
  }

  // GET User Role List
  public getUserRoleList(hospid: number): Observable<DataResult> {
    if (environment.UseBusinessServerForSiteManagement) {
      return this.getBusinessUserRoleList(hospid);
    } else {
      return this.getLocalUserRoleList(hospid);
    }
  }

  private getLocalUserRoleList(hospid: number): Observable<DataResult> {
    const uri = `${this._LOCAL_API}${this._LOCAL_USER_ROLE_LIST_FILENAME}`;

    return this.http.get<DataResult>(
      uri, { observe: 'response' }
    )
      .pipe(
        map(response => {

          delay(5000);

          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }

          return result;
        }),
        catchError(error => {
          return this.handleError(`getLocalUserRoleList failed`, error, hospid);
        }),
        take(1)
      );
  }

  private getBusinessUserRoleList(hospid: number): Observable<DataResult> {
    const uri = `${this._BASE_URL}SiteCoordinator/GetRolesLU?facID=${hospid}`;
    const media = environment.GetUserRoleMedia;
    // const media = 'application/json';
    return this.http
      .get<DataResult>(uri,
        {
          headers: { Accept: media },
          observe: 'response'
        }
      )
      .pipe(
        map(response => {
          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }

          return result;
        }),
        catchError(error => {
          return this.handleError(`getBusinessUserRoleList failed`, error, hospid);
        }),
        take(1)
      );
  }

  // Save user
  public saveUser(userDetails: UserDetailResult): Observable<UserDetailResult> {
    if (environment.UseBusinessServerForSiteManagement) {
      return this.saveUserBusiness(userDetails);
    } 
    else {
      return this.saveUserLocal(userDetails);
    } 
  }

  private saveUserBusiness(userdetailResult: UserDetailResult): Observable<UserDetailResult> {
    
    const uri = `${this._BASE_URL}SiteCoordinator/UpdateUser`;
    const media = environment.UpdateUserMedia;

    return this.http.put<UserDetailResult>(uri, userdetailResult.userdetails,
      {
        headers: { Accept: media },
        observe: 'response'
      }
    )
    .pipe(
      map((response: HttpResponse<UserDetailResult>) => {
        let result: UserDetailResult;
        if (response instanceof HttpResponse) {
          if (response.status === 200) {              
            const detail: any = response.body;
            const detailResult = new UserDetailResult(
              response.ok,
              response.status,
              response.statusText,
              detail
            );
            result = detailResult;
          }
        }
        if (response instanceof HttpErrorResponse) {
          result = new UserDetailResult(response.ok, response.status, response.statusText, userdetailResult.UserDetails);
        }

        return result;
      }),
      catchError(error => {
           return this.handleError(`saveUser failed`, error, null);
        }),
      take(1)
    );
      
}

  private saveUserLocal(userdetailResult: UserDetailResult): Observable<UserDetailResult> {
    
    const uri = `${this._LOCAL_API}${this._LOCAL_SAVE_USER_FILENAME}`;

    return this.http.put<UserDetailResult>(uri, userdetailResult.userdetails,
        {
          observe: 'response'
        }
      )
      .pipe(
        map((response: HttpResponse<UserDetailResult>) => {
          let result: UserDetailResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {              
              const detail: any = response.body;
              const detailResult = new UserDetailResult(
                response.ok,
                response.status,
                response.statusText,
                detail
              );
              result = detailResult;
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new UserDetailResult(response.ok, response.status, response.statusText, userdetailResult.UserDetails);
          }

          return result;
        }),
        catchError(error => {
             return this.handleError(`saveUser failed`, error, null);
          }),
        take(1)
      );
  }

  public deleteUser(facilityUserID: number): Observable<any> {
    if (environment.UseBusinessServerForSiteManagement) {
      return this.deleteBusinessUser(facilityUserID);
    } else {
      return this.deleteLocalUser(facilityUserID);
    }
  }

  private deleteBusinessUser(facilityUserID: number): Observable<any> {
    //SiteCoordinator/DeleteUser?facilityUserID=333
    const uri = `${this._BASE_URL}SiteCoordinator/DeleteUser?facUserID=${facilityUserID}`;
    const media = environment.DeleteUserMedia;

    return this.http
      .get<any>(uri,
        {
          headers: { Accept: media },
          observe: 'response'
        }
      )
      .pipe(
        map(response => {
          let result: any;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = response.body;
            }
          }
          return result;
        }),
        catchError(error => {
          return this.handleError(`deleteUser failed`, error, null);
        }),
        take(1)
      );
  }

  private deleteLocalUser(facilityUserID: number): Observable<any> {
    
    const uri = `${this._LOCAL_API}${this._LOCAL_DELETE_USER_FILENAME}`;

    return this.http.get<DataResult>(
      uri, { observe: 'response' }
    )
      .pipe(
        map(response => {

          delay(5000);

          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }

          return result;
        }),
        catchError(error => {
          return this.handleError(`deleteUserLocal failed`, error, facilityUserID);
        }),
        take(1)
      );
  }

  // Main Menu Access Tab
  public getMainMenuAccess(facilityUserID: number): Observable<DataResult> {
    if (environment.UseBusinessServerForSiteManagement) {
      return this.getBusinessMainMenuAccess(facilityUserID);
    } else {
      return this.getLocalMainMenuAccess(facilityUserID);
    }
  }

  private getLocalMainMenuAccess(facilityUserID: number): Observable<DataResult> {
    const uri = `${this._LOCAL_API}${this._LOCAL_MAIN_MENU_ACCESS_FILENAME}`;

    return this.http.get<DataResult>(
      uri, { observe: 'response' }
    )
      .pipe(
        map(response => {

          delay(5000);

          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }

          return result;
        }),
        catchError(error => {
          return this.handleError(`getLocalMainMenuAccess failed`, error, facilityUserID);
        }),
        take(1)
      );
  }

  private getBusinessMainMenuAccess(facilityUserID: number): Observable<DataResult> {
    const uri = `${this._BASE_URL}SiteCoordinator/GetMainMenuAccess?facUserID=${facilityUserID}`; 
    const media = environment.GetMainMenuAccessMedia;
    // const media = 'application/json';
    return this.http
      .get<DataResult>(uri,
        {
          headers: { Accept: media },
          observe: 'response'
        }
      )
      .pipe(
        map(response => {
          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }

          return result;
        }),
        catchError(error => {
          return this.handleError(`getBusinessMainMenuAccess failed`, error, facilityUserID);
        }),
        take(1)
      );
  }

   // Data Entry Access Tab
   public getDataEntryAccess(facilityUserID: number): Observable<DataResult> {
    if (environment.UseBusinessServerForSiteManagement) {
      return this.getBusinessDataEntryAccess(facilityUserID);
    } else {
      return this.getLocalDataEntryAccess(facilityUserID);
    }
  }

  private getLocalDataEntryAccess(facilityUserID: number): Observable<DataResult> {
    const uri = `${this._LOCAL_API}${this._LOCAL_DATA_ENTRY_ACCESS_FILENAME}`;

    return this.http.get<DataResult>(
      uri, { observe: 'response' }
    )
      .pipe(
        map(response => {

          delay(5000);

          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }

          return result;
        }),
        catchError(error => {
          return this.handleError(`getLocalDataEntryAccess failed`, error, facilityUserID);
        }),
        take(1)
      );
  }

  private getBusinessDataEntryAccess(facilityUserID: number): Observable<DataResult> {
    const uri = `${this._BASE_URL}SiteCoordinator/GetDataEntryAccess?facUserID=${facilityUserID}`; 
    const media = environment.GetDataEntryAccessMedia;
    // const media = 'application/json';
    return this.http
      .get<DataResult>(uri,
        {
          headers: { Accept: media },
          observe: 'response'
        }
      )
      .pipe(
        map(response => {
          let result: DataResult;
          if (response instanceof HttpResponse) {
            if (response.status === 200) {
              result = new DataResult(response.ok, response.status, response.statusText, response.body);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
          }

          return result;
        }),
        catchError(error => {
          return this.handleError(`getBusinessDataEntryAccess failed`, error, facilityUserID);
        }),
        take(1)
      );
  }

  
  private handleError(operation: string, error: any, _id: number) {
    let result: any;
     this.logger.error('set-management.service', 'handleError', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, `Operation:${operation}`, error.message);
    if (error instanceof HttpErrorResponse) {
      const errorResponse = error as HttpErrorResponse;
      if (errorResponse && (errorResponse.status === 401)) {
        throw errorResponse;
      } else {
        const empty = new DataResult(false, 0, error.message, this._NO_DATA);
        result = empty;
      }
    }
    return of(result);
  }
}
